import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, Show } from "solid-js";
import { ArrowLeft, IdCard } from "lucide-solid";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsPanel, TabsPanels, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "@/lib/u-router";
import { api, createApiQuery } from "@/lib/api";
import { adminQueryKeys, type components } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { ProfileTab } from "./profile-tab";
import { MembershipsTab } from "./memberships-tab";
import { GuardiansTab } from "./guardians-tab";
import { SessionsTab } from "./sessions-tab";
import { DangerZoneTab } from "./danger-zone-tab";

type ErrorModel = components["schemas"]["ErrorModel"];
type DirectoryResponse = components["schemas"]["DirectoryUserListResponse"];
type MembershipResponse = components["schemas"]["UserMembershipListResponse"];
export type DirectoryUser = components["schemas"]["DirectoryUserDTO"];
export type Membership = components["schemas"]["UserMembershipDTO"];

/**
 * F2-C — admin user detail. Tabbed view: perfil / roles-membresías /
 * representantes / sesiones-dispositivos / danger zone (suspend, impersonate).
 * Reuses the directory endpoint (found by id) + the per-user memberships endpoint.
 */
export function UserDetailView(props: { userId: string }) {
  const { t } = useI18n();
  const [tab, setTab] = createSignal("profile");

  // The directory has no by-id read; fetch it and find the row. Small admin
  // lists → acceptable; the query is cached under the shared users key.
  const usersQuery = createApiQuery<DirectoryResponse, ErrorModel>(
    () => adminQueryKeys.users(),
    async () => api.GET("/api/admin/users", {}),
    () => ({ staleTime: 15_000 }),
  );
  const membershipsQuery = createApiQuery<MembershipResponse, ErrorModel>(
    () => ["admin", "user-memberships", props.userId],
    async () =>
      api.GET("/api/admin/users/{id}/memberships", { params: { path: { id: props.userId } } }),
    () => ({ staleTime: 15_000 }),
  );

  const user = createMemo<DirectoryUser | undefined>(() =>
    usersQuery.data?.data.find((u) => u.id === props.userId),
  );
  const memberships = createMemo<Membership[]>(() => membershipsQuery.data?.data ?? []);

  return (
    <div class="space-y-6">
      <Link
        to="/admin/users"
        class="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft class="h-4 w-4" />
        {t(msg`Back to users`)}
      </Link>

      <Show when={!usersQuery.isLoading} fallback={<Skeleton class="h-28 w-full rounded-xl" />}>
        <Show
          when={user()}
          fallback={
            <Card>
              <CardContent class="p-8 text-center text-sm text-muted-foreground">
                {t(msg`User not found.`)}
              </CardContent>
            </Card>
          }
        >
          {(u) => (
            <>
              <Card>
                <CardContent class="p-5 flex items-center gap-4">
                  <div class="h-14 w-14 shrink-0 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-xl font-bold uppercase">
                    {(u().name || u().email).charAt(0)}
                  </div>
                  <div class="min-w-0">
                    <div class="flex items-center gap-2 flex-wrap">
                      <h2
                        class="text-2xl font-bold tracking-tight truncate"
                        data-testid="user-detail-name"
                      >
                        {u().name || t(msg`(no name)`)}
                      </h2>
                      <Badge variant="secondary" class="capitalize">
                        {u().role}
                      </Badge>
                      <Show when={u().banned}>
                        <Badge variant="destructive">{t(msg`Suspended`)}</Badge>
                      </Show>
                    </div>
                    <div class="text-sm text-muted-foreground truncate">{u().email}</div>
                    <Show when={u().nationalId}>
                      <div class="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                        <IdCard class="h-3.5 w-3.5" />
                        {u().nationalId}
                      </div>
                    </Show>
                  </div>
                </CardContent>
              </Card>

              <Tabs value={tab()} onChange={setTab}>
                <TabsList>
                  <TabsTrigger value="profile">{t(msg`Profile`)}</TabsTrigger>
                  <TabsTrigger value="roles">{t(msg`Roles & memberships`)}</TabsTrigger>
                  <TabsTrigger value="guardians">{t(msg`Guardians`)}</TabsTrigger>
                  <TabsTrigger value="sessions">{t(msg`Sessions & devices`)}</TabsTrigger>
                  <TabsTrigger value="danger">{t(msg`Danger zone`)}</TabsTrigger>
                </TabsList>
                <TabsPanels
                  value={tab()}
                  order={["profile", "roles", "guardians", "sessions", "danger"]}
                >
                  <TabsPanel value="profile">
                    <ProfileTab user={u()} />
                  </TabsPanel>
                  <TabsPanel value="roles">
                    <MembershipsTab
                      user={u()}
                      memberships={memberships()}
                      loading={membershipsQuery.isLoading}
                    />
                  </TabsPanel>
                  <TabsPanel value="guardians">
                    <GuardiansTab userId={u().id} memberships={memberships()} />
                  </TabsPanel>
                  <TabsPanel value="sessions">
                    <SessionsTab userId={u().id} />
                  </TabsPanel>
                  <TabsPanel value="danger">
                    <DangerZoneTab user={u()} onChanged={() => void usersQuery.refetch()} />
                  </TabsPanel>
                </TabsPanels>
              </Tabs>
            </>
          )}
        </Show>
      </Show>
    </div>
  );
}
